// src/components/shared/DashboardTradeModal.jsx
import { HiX } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';

const formatDate = (timestamp, lang) => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp).toLocaleString(lang === 'ru' ? 'ru-RU' : 'en-US', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).replace(',', '');
};

const PnlValue = ({ value }) => (
    <span className={`font-mono font-semibold ${value >= 0 ? 'text-green-400' : 'text-red-400'}`}>
        {value >= 0 ? '+' : ''}{value?.toFixed(2) || '0.00'} $
    </span>
);

const DashboardTradeModal = ({ trade, onClose }) => {
  const { t, i18n } = useTranslation();
  if (!trade || !trade.exchangeData) return null;

  const exData = trade.exchangeData;
  const realized = exData.realized || {};
  const entries = exData.entries || [];
  
  // Группируем итоги по символам для секции Exit
  const realizedMap = {};
  if (realized.legs) {
    realized.legs.forEach(leg => {
      realizedMap[leg.symbol] = leg;
    });
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[200] p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-5xl text-white border border-slate-700 flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="p-6 border-b border-slate-700 flex justify-between items-center bg-slate-800/50 rounded-t-2xl">
          <div>
            <h2 className="text-xl font-bold text-white">{trade.customStrategyName || trade.strategyName}</h2>
            <p className="text-xs text-slate-400 mt-1 uppercase tracking-widest">{trade.exchange} execution</p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-full transition-all">
            <HiX className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto custom-scrollbar space-y-8">
          
          {/* Section: Entries */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
              {t('trades.modal.entries_title')} ({entries.length})
            </h3>
            <div className="grid gap-3">
              {entries.map((entry, index) => (
                <div key={index} className="bg-slate-900/50 border border-slate-700/30 p-4 rounded-xl grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                  <div>
                    <span className="text-[10px] text-slate-500 block uppercase font-bold">{t('trades.modal.entry_num')}{index + 1}</span>
                    <span className="text-sm font-medium">{formatDate(entry.entryTime, i18n.language)}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-500 block uppercase font-bold">Z-Score / Level</span>
                    <span className="font-mono text-sm text-blue-400">{entry.z?.toFixed(4)} <span className="text-slate-600 px-1">/</span> {entry.level}</span>
                  </div>
                  {entry.legs?.map((leg, lIdx) => (
                    <div key={lIdx} className="font-mono text-xs p-2 bg-slate-800/50 rounded border border-slate-700/50">
                      <div className="flex justify-between mb-1">
                        <span className={leg.side === 'Buy' ? 'text-green-400' : 'text-rose-400'}>{leg.symbol} {leg.side.toUpperCase()}</span>
                        <span className="text-slate-300">{leg.qty}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Section: Realized Outcomes (Exit & PNL) */}
          {realized.legs && (
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                {t('trades.modal.exit_pnl_title')}
              </h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {realized.legs.map((leg, idx) => (
                  <div key={idx} className="bg-slate-900/40 border border-slate-700/50 p-5 rounded-xl">
                    <div className="flex justify-between items-center mb-4 border-b border-slate-700/50 pb-2">
                      <span className="text-lg font-bold text-white">{leg.symbol}</span>
                      <span className="text-xs bg-slate-800 px-2 py-1 rounded text-slate-400">Qty: {leg.qty}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-y-4 text-sm">
                      <div>
                        <p className="text-slate-500 text-[10px] uppercase font-bold mb-1">Avg Entry Price</p>
                        <p className="font-mono">${leg.avgEntryPrice?.toFixed(5)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-[10px] uppercase font-bold mb-1">Avg Exit Price</p>
                        <p className="font-mono text-emerald-400">${leg.avgExitPrice?.toFixed(5)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-[10px] uppercase font-bold mb-1">Total Fees</p>
                        <p className="font-mono text-rose-400">-${(leg.openFee + leg.closeFee).toFixed(4)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-[10px] uppercase font-bold mb-1">Net PNL</p>
                        <PnlValue value={leg.closedPnl} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Grand Total Row */}
              <div className="mt-6 bg-blue-600/10 border border-blue-500/20 p-6 rounded-2xl flex flex-col md:flex-row justify-between items-center gap-4 shadow-inner">
                <div className="flex items-center gap-6">
                  <div>
                    <p className="text-slate-500 text-[10px] uppercase font-bold mb-1">Total Trading Fees</p>
                    <p className="font-mono text-lg text-white">-${realized.totalFees?.toFixed(4)}</p>
                  </div>
                  <div className="w-px h-10 bg-slate-700 hidden md:block"></div>
                  <div>
                    <p className="text-slate-500 text-[10px] uppercase font-bold mb-1">Execution Status</p>
                    <p className="text-emerald-400 font-bold uppercase tracking-widest text-sm">{exData.status}</p>
                  </div>
                </div>
                <div className="text-center md:text-right">
                  <p className="text-blue-300 text-[10px] uppercase font-black mb-1 tracking-widest">{t('trades.modal.total_pnl')}</p>
                  <div className="text-3xl font-black font-mono">
                    <PnlValue value={realized.totalClosedPnl} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardTradeModal;